import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faImage, 
  faMusic, 
  faVolumeHigh, 
  faMicrophone,
  faUsers,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { ScriptAnalyzer } from '../utils/script-analyzer.util';

interface ScriptData {
  id: string;
  generatedScript: string;
  userSelections: {
    userIdea: string;
  };
}

interface PromptTypeOption {
  id: string;
  label: string;
  description: string;
  icon: any;
  color: string;
  estimatedCount: number;
}

interface PromptTypeSelectorProps {
  script: ScriptData;
  selectedTypes: string[];
  onTypeSelect: (types: string[]) => void;
  useConsistentCharacters?: boolean;
  onCharacterConsistencyChange?: (enabled: boolean) => void;
}

const PromptTypeSelector: React.FC<PromptTypeSelectorProps> = ({
  script,
  selectedTypes,
  onTypeSelect,
  useConsistentCharacters = false,
  onCharacterConsistencyChange
}) => {
  const { t } = useTranslation('scriptGenerator');
  
  // Use script analyzer to get prompt estimates
  const estimates = ScriptAnalyzer.getPromptEstimates(script.generatedScript);
  const breakdown = ScriptAnalyzer.parseSceneBreakdown(script.generatedScript);

  const promptTypes: PromptTypeOption[] = [
    {
      id: 'image',
      label: t('scriptGenerator.promptTypeSelector.imagePrompts'),
      description: t('scriptGenerator.promptTypeSelector.imageDescription'),
      icon: faImage,
      color: '#3b82f6',
      estimatedCount: estimates.image
    },
    {
      id: 'music',
      label: t('scriptGenerator.promptTypeSelector.musicPrompts'),
      description: t('scriptGenerator.promptTypeSelector.musicDescription'),
      icon: faMusic,
      color: '#8b5cf6',
      estimatedCount: estimates.music
    },
    {
      id: 'sfx',
      label: t('scriptGenerator.promptTypeSelector.soundEffectPrompts'),
      description: t('scriptGenerator.promptTypeSelector.soundEffectDescription'),
      icon: faVolumeHigh,
      color: '#10b981',
      estimatedCount: estimates.sfx
    },
    {
      id: 'narration',
      label: t('scriptGenerator.promptTypeSelector.narrationPrompts'),
      description: t('scriptGenerator.promptTypeSelector.narrationDescription'),
      icon: faMicrophone,
      color: '#f59e0b',
      estimatedCount: estimates.narration
    },
    {
      id: 'characters',
      label: t('scriptGenerator.promptTypeSelector.characterPrompts'),
      description: useConsistentCharacters 
        ? t('scriptGenerator.promptTypeSelector.characterPromptsDescription')
        : t('scriptGenerator.promptTypeSelector.characterPromptsDisabled'),
      icon: faUsers,
      color: '#ef4444',
      estimatedCount: estimates.characters
    }
  ];

  // Character prompts are now included in auto-generation from scripts

  const handleTypeToggle = (typeId: string) => {
    // Don't allow selecting characters if consistent characters is disabled
    if (typeId === 'characters' && !useConsistentCharacters) {
      return;
    }
    
    const newSelection = selectedTypes.includes(typeId)
      ? selectedTypes.filter(id => id !== typeId)
      : [...selectedTypes, typeId];
    
    onTypeSelect(newSelection);
  };

  const handleSelectAll = () => {
    // Filter out character prompts if consistent characters is disabled
    const availableTypes = promptTypes.filter(type => 
      type.id !== 'characters' || useConsistentCharacters
    );
    
    const availableTypeIds = availableTypes.map(type => type.id);
    
    if (selectedTypes.length === availableTypeIds.length) {
      onTypeSelect([]);
    } else {
      onTypeSelect(availableTypeIds);
    }
  };

  return (
    <div style={{ marginTop: '16px' }}>
      {/* Character Consistency Option - Show before prompt selection */}
      {breakdown && breakdown.characters.length > 0 && (
        <div style={{
          marginBottom: '16px',
          padding: '16px',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: '8px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '8px'
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              color: '#e0e0e0'
            }}>
              <input
                type="checkbox"
                checked={useConsistentCharacters}
                onChange={(e) => onCharacterConsistencyChange?.(e.target.checked)}
                style={{
                  width: '16px',
                  height: '16px',
                  accentColor: '#3b82f6'
                }}
              />
              🎭 {t('scriptGenerator.promptTypeSelector.useConsistentCharacters')}
              <span style={{
                fontSize: '10px',
                fontWeight: '600',
                background: 'linear-gradient(135deg, #ff6b6b, #ffa500)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                padding: '2px 6px',
                border: '1px solid rgba(255, 107, 107, 0.3)',
                borderRadius: '12px',
                backdropFilter: 'blur(10px)',
                position: 'relative',
                marginLeft: '8px'
              }}>
                {t('scriptGenerator.promptTypeSelector.beta')}
              </span>
            </label>
          </div>
          
          <p style={{
            margin: '0 0 8px 0',
            fontSize: '12px',
            color: '#a0a0a0',
            lineHeight: '1.4'
          }}>
            {t('scriptGenerator.promptTypeSelector.characterDescription')}
          </p>
          
          {useConsistentCharacters && (
            <div style={{
              fontSize: '11px',
              color: '#93c5fd',
              fontStyle: 'italic'
            }}>
              {t('scriptGenerator.promptTypeSelector.characterNote')}
            </div>
          )}
        </div>
      )}
      
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '16px'
      }}>
        <h4 style={{
          margin: 0,
          color: '#e0e0e0',
          fontSize: '14px',
          fontWeight: '600'
        }}>
          {t('scriptGenerator.promptTypeSelector.promptTypesToGenerate')}
        </h4>
        
        <button
          onClick={handleSelectAll}
          style={{
            background: 'none',
            border: '1px solid #555',
            color: '#a0a0a0',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#3b82f6';
            e.currentTarget.style.color = '#3b82f6';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#555';
            e.currentTarget.style.color = '#a0a0a0';
          }}
        >
          {(() => {
            const availableTypes = promptTypes.filter(type => 
              type.id !== 'characters' || useConsistentCharacters
            );
            const availableTypeIds = availableTypes.map(type => type.id);
            
            return selectedTypes.length === availableTypeIds.length 
              ? t('scriptGenerator.promptTypeSelector.deselectAll') 
              : t('scriptGenerator.promptTypeSelector.selectAll');
          })()}
        </button>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '12px'
      }}>
        {promptTypes.map((type) => {
          const isSelected = selectedTypes.includes(type.id);
          const isDisabled = type.id === 'characters' && !useConsistentCharacters;
          
          return (
            <div
              key={type.id}
              onClick={() => handleTypeToggle(type.id)}
              style={{
                background: isDisabled
                  ? 'rgba(255, 255, 255, 0.02)'
                  : isSelected 
                    ? `rgba(${type.color === '#3b82f6' ? '59, 130, 246' : 
                               type.color === '#8b5cf6' ? '139, 92, 246' :
                               type.color === '#10b981' ? '16, 185, 129' :
                               type.color === '#f59e0b' ? '245, 158, 11' :
                               '239, 68, 68'}, 0.1)`
                    : 'rgba(255, 255, 255, 0.05)',
                border: isDisabled
                  ? '2px solid rgba(255, 255, 255, 0.05)'
                  : isSelected 
                    ? `2px solid ${type.color}` 
                    : '2px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                padding: '16px',
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                position: 'relative',
                opacity: isDisabled ? 0.4 : 1
              }}
              onMouseEnter={(e) => {
                if (!isSelected && !isDisabled) {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                  e.currentTarget.style.borderColor = `${type.color}40`;
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected && !isDisabled) {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                }
              }}
            >
              {isSelected && (
                <FontAwesomeIcon 
                  icon={faCheckCircle} 
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    color: type.color,
                    fontSize: '16px'
                  }}
                />
              )}
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '8px'
              }}>
                <FontAwesomeIcon 
                  icon={type.icon} 
                  style={{ 
                    color: type.color, 
                    fontSize: '20px' 
                  }} 
                />
                
                <h5 style={{
                  margin: 0,
                  color: '#e0e0e0',
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  {type.label}
                </h5>
              </div>
              
              <p style={{
                margin: '0 0 8px 0',
                color: '#a0a0a0',
                fontSize: '12px',
                lineHeight: '1.4'
              }}>
                {type.description}
              </p>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '11px',
                color: '#888'
              }}>
                <span>~{type.estimatedCount}</span>
                <span>{type.estimatedCount === 1 ? t('scriptGenerator.promptTypeSelector.prompt') : t('scriptGenerator.promptTypeSelector.prompts')}</span>
              </div>
            </div>
          );
        })}
      </div>

      {selectedTypes.length > 0 && (
        <div style={{
          marginTop: '16px',
          padding: '12px',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: '6px',
          fontSize: '13px',
          color: '#e0e0e0'
        }}>
          <strong>Selected:</strong> {selectedTypes.length} prompt type
          {selectedTypes.length > 1 ? 's' : ''} will be generated
          <span style={{ color: '#a0a0a0', marginLeft: '8px' }}>
            (~{selectedTypes.reduce((total, typeId) => {
              const type = promptTypes.find(t => t.id === typeId);
              return total + (type?.estimatedCount || 0);
            }, 0)} individual prompts)
          </span>
        </div>
      )}
    </div>
  );
};

export default PromptTypeSelector;